
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSubscription } from "../hooks/useSubscription";

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const { status, isLoading, checkSubscriptionStatus } = useSubscription();
  
  // Refresh subscription status when the page loads
  useEffect(() => {
    checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);
  
  return (
    <>
      <Helmet>
        <title>Subscription Successful | SaunaDip</title>
        <meta name="description" content="Thank you for subscribing to SaunaDip" />
        <meta name="robots" content="noindex" />
      </Helmet>
      
      <div className="bg-sauna-beige min-h-screen py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <CheckCircle2 size={80} className="mx-auto mb-6 text-green-600" />
          
          <h1 className="text-4xl font-bold text-sauna-navy mb-6">Thank You for Subscribing!</h1>
          
          <Card className="mb-8 shadow-md border-0">
            <CardContent className="pt-6">
              <div className="space-y-6">
                <p className="text-xl">
                  Your {!isLoading && status?.current_plan.name} plan is now active.
                </p>
                
                <div className="bg-green-50 rounded-lg p-4 text-green-700 text-sm border border-green-100">
                  You now have access to all the premium features included in your plan. Enjoy enhanced visibility and tools for your sauna business!
                </div>
                
                <div className="grid gap-6 md:grid-cols-2 mt-8">
                  <div className="bg-white p-6 rounded-lg border border-gray-100 text-left">
                    <h3 className="font-semibold text-lg mb-3">Next Steps</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2">
                        <div className="rounded-full bg-sauna-orange/10 p-1 mt-0.5">
                          <span className="text-sauna-orange text-xs font-bold">1</span>
                        </div>
                        <span>Complete your operator profile</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="rounded-full bg-sauna-orange/10 p-1 mt-0.5">
                          <span className="text-sauna-orange text-xs font-bold">2</span>
                        </div>
                        <span>Upload gallery photos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="rounded-full bg-sauna-orange/10 p-1 mt-0.5">
                          <span className="text-sauna-orange text-xs font-bold">3</span>
                        </div>
                        <span>Add your services and offerings</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-white p-6 rounded-lg border border-gray-100 text-left">
                    <h3 className="font-semibold text-lg mb-3">Need Help?</h3>
                    <p className="text-gray-600 mb-4">
                      Our team is here to help you get the most out of your subscription. Feel free to contact us with any questions.
                    </p>
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/contact">Contact Support</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="space-y-4">
            <Button 
              asChild
              size="lg" 
              className="bg-sauna-orange hover:bg-sauna-orange/90 px-8"
            >
              <Link to="/operator-onboarding">
                Get Started <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
            <p>
              <Button asChild variant="ghost" size="sm" className="text-gray-500">
                <Link to="/subscription">Manage Subscription</Link>
              </Button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionSuccess;
